<template>
    <div class="mt-6 content">
        <div class="is-flex is-justify-content-space-between is-align-items-flex-start">
            <div class="columns is-gapless is-mobile mr-6" style="max-width: 80%;">
                <div class="column is-narrow"><i class="fad fa-fw fa-folder-arrow-up has-text-white mr-3 is-size-4"></i></div>
                <div class="column">
                    <h3 class="has-text-white">Files &amp; Uploads</h3>
                    <p class="has-text-white">Some assignments ask you to create or work on certain documents. You can upload files here and keep them stored with your course enrollment.</p>
                </div>
            </div>
            <b-button type="is-success" class="ml-4" :class="{'is-invisible': loading}" @click="showUploadPanel = true">
                <i class="fas fa-plus mr-2"></i>Upload File
            </b-button>
        </div>

        <!-- Loading -->
        <div class="mt-6 pb-6" v-if="loading">
            <ui-card class="pt-0">
                <div class="py-2 has-text-centered">
                    <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading files...
                </div>
            </ui-card>
        </div>

        <div class="mt-6 pb-6" v-else>
            <!-- File list -->
            <ui-card class="pt-0">
                <div v-if="files.length === 0" class="columns is-gapless pt-2">
                    <div class="column is-narrow"><i class="far fa-fw fa-folder-open mr-2"></i></div>
                    <div class="column">
                        <div class="has-text-weight-bold">You haven't uploaded any files to this course yet.</div>
                        <div class="mt-1">Click <em>Upload File</em> to add your first one.</div>
                    </div>
                </div>

                <div v-else>
                    <div class="file-row" v-for="file in files" :key="file.id" @click="openFile(file)">
                        <div class="columns is-mobile is-vcentered is-gapless">
                            <div class="column is-narrow" style="align-self: flex-start;">
                                <i class="far fa-fw fa-file mr-3 is-size-5 has-text-grey"></i>
                            </div>
                            <div class="column">
                                <div class="has-text-weight-bold">
                                    {{ file.title }}
                                    <b-tag v-if="file.milestone_number" class="ml-2" size="is-small" type="is-info is-light">
                                        Milestone {{ file.milestone_number }}
                                    </b-tag>
                                    <b-tag v-if="isStemFile(file)" class="ml-2" size="is-small" type="is-success is-light">
                                        <i class="fas fa-flask mr-1"></i>STEM
                                    </b-tag>
                                </div>
                                <div class="is-size-7 has-text-grey">{{ formatDate(file.created_at) }} &middot; {{ formatFileSize(file.size) }}</div>
                            </div>
                            <div class="column is-narrow">
                                <a :href="file.download_url" class="row-action mr-2" title="Download" @click.stop>
                                    <i class="fas fa-download"></i>
                                </a>
                                <span v-if="file.deletable" class="row-action delete-x is-clickable" title="Delete"
                                      @click.stop="deleteFile(file)">
                                    <i class="fas fa-times"></i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </ui-card>
        </div>

        <!-- Upload drawer (shared with milestone attachments) -->
        <file-upload-panel
            :is-open="showUploadPanel"
            :enrollment-id="enrollmentId"
            :is-stem="isStem"
            @close="showUploadPanel = false"
            @uploaded="onUploaded"
        />
    </div>
</template>

<script>
    // Deletes go through the raw axios instance — cachios would key the
    // FormData body as '{}' and serve a stale cached response.
    import {client as http, axiosInstance} from "../../http_client";
    import moment from "moment";
    import UiCard from "../../components/UiCard";
    import FileUploadPanel from "../../components/course/FileUploadPanel";

    export default {
        components: {UiCard, FileUploadPanel},
        props: ['enrollmentId'],
        data: function() {
            return {
                loading: true,
                files: [],
                isStem: false,
                showUploadPanel: false
            };
        },
        methods: {
            loadFiles: function() {
                http.get(`/app/enrollments/${this.enrollmentId}/files`, {force: true}).then((response) => {
                    this.files = response.data.files || [];
                    this.isStem = response.data.stem === true;
                    this.loading = false;
                }).catch((error) => {
                    console.error('Error loading files:', error);
                    this.loading = false;
                });
            },
            onUploaded: function() {
                this.showUploadPanel = false;
                this.loadFiles();
            },
            openFile: function(file) {
                window.open(file.preview_url || file.download_url, '_blank', 'noopener');
            },
            isStemFile: function(file) {
                return /^STEM_/i.test(file.filename || '') || /^STEM_/i.test(file.title || '');
            },
            deleteFile: function(file) {
                this.$buefy.dialog.confirm({
                    title: 'Delete File',
                    message: `Are you sure you want to delete <strong>${this.escapeHtml(file.title)}</strong>? This cannot be undone.`,
                    confirmText: 'Delete',
                    type: 'is-danger',
                    onConfirm: () => {
                        axiosInstance.delete(`/app/enrollments/${this.enrollmentId}/files/${file.id}`).then(() => {
                            this.$buefy.toast.open({
                                message: 'File deleted',
                                type: 'is-success'
                            });
                            this.loadFiles();
                        }).catch((error) => {
                            console.error('Error deleting file:', error);
                            this.$buefy.toast.open({
                                message: error.response?.data?.error || 'Error deleting file',
                                type: 'is-danger'
                            });
                        });
                    }
                });
            },
            formatFileSize: function(bytes) {
                if (!bytes && bytes !== 0) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },
            formatDate: function(date) {
                return date ? moment(date).format('MMM D, YYYY, h:mm a') : '';
            },
            escapeHtml: function(text) {
                const div = document.createElement('div');
                div.textContent = text;
                return div.innerHTML;
            }
        },
        mounted() {
            this.loadFiles();
        }
    };
</script>

<style scoped>
    /* Dividers between rows only — matches the notes list */
    .file-row {
        padding: 0.75rem 0.5rem;
        border-bottom: 1px solid #e7e7e7;
        cursor: pointer;
    }
    .file-row:last-child {
        border-bottom: none;
    }
    .file-row:hover {
        background-color: #f7f7f7;
    }

    /* Lightweight row actions — shared look with the Notes and Study Log lists */
    .row-action {
        display: inline-block;
        font-size: 1.25rem;
        line-height: 1;
        color: #7a7a7a;
        padding: 0.3rem 0.45rem;
        border: 1px solid #dbdbdb;
        border-radius: 4px;
        transition: color 0.2s, border-color 0.2s;
    }
    .row-action:hover {
        color: #363636;
        border-color: #b5b5b5;
    }
    .row-action.delete-x {
        color: #f14668;
    }
    .row-action.delete-x:hover {
        color: #cc0f35;
    }
</style>
